/**
 * Servicio para gestión de pagos
 * 
 * Este servicio maneja todas las operaciones relacionadas con pagos:
 * - Iniciar pagos con Redsys
 * - Pagos con monedero
 * - Obtener transacciones
 * - Gestionar estados de pago
 */

import api from './api';

const ENDPOINTS = {
  INIT_REDSYS: '/payments/redsys/init',
  PROCESS_REDSYS_RESPONSE: '/payments/redsys/process-response',
  TRANSACTIONS: '/payments/transactions',
  TRANSACTION_BY_ID: (id) => `/payments/transactions/${id}`,
  REFUND: (id) => `/payments/transactions/${id}/refund`,
  STATS: '/payments/stats',
  WALLET_ADD_FUNDS: '/wallet/add-funds',
  WALLET_PAY_MATCH: '/wallet/pay-match',
  WALLET_BALANCE: '/wallet/balance',
  WALLET_TRANSACTIONS: '/wallet/transactions',
  // Endpoints para administración de transferencias
  ADMIN_TRANSACTIONS: '/payments/admin/transactions',
  TRANSFER_STATS: '/payments/admin/transfer-stats',
  VALIDATE_TRANSFER: (id) => `/payments/admin/transfers/${id}/validate`,
  REJECT_TRANSFER: (id) => `/payments/admin/transfers/${id}/reject`,
  FINANCIAL_REPORTS: '/payments/admin/financial-reports',
  DIRECT_INSCRIPTION: '/payments/admin/direct-inscription',
  CREATE_PENDING: '/payments/create-pending-transaction'
};

const paymentService = {
  /**
   * Iniciar pago con Redsys
   */
  initRedsysPayment: async (paymentData) => {
    try {
      console.log('🔄 initRedsysPayment - Enviando datos:', paymentData);
      console.log('🌐 Endpoint:', ENDPOINTS.INIT_REDSYS);

      const response = await api.post(ENDPOINTS.INIT_REDSYS, paymentData);

      console.log('📥 Respuesta del servidor:', response);
      console.log('📦 Datos de respuesta:', response.data);

      return response.data;
    } catch (error) {
      console.error('❌ Error al iniciar pago con Redsys:', error);
      console.error('❌ Error response:', error.response);
      console.error('❌ Error data:', error.response?.data);
      throw error;
    }
  },

  /**
   * Procesar respuesta de Redsys
   */
  processRedsysResponse: async (redsysParams) => {
    try {
      const response = await api.post(ENDPOINTS.PROCESS_REDSYS_RESPONSE, redsysParams);
      return response.data;
    } catch (error) {
      console.error('❌ Error al procesar respuesta de Redsys:', error);
      throw error;
    }
  },

  /**
   * Añadir fondos al monedero
   */
  addFundsToWallet: async (amount, paymentMethod = 'redsys') => {
    try {
      const response = await api.post(ENDPOINTS.WALLET_ADD_FUNDS, {
        amount,
        paymentMethod
      });
      return response.data;
    } catch (error) {
      console.error('❌ Error al añadir fondos al monedero:', error);
      throw error;
    }
  },

  /**
   * Pagar con monedero
   */
  payWithWallet: async (paymentData) => {
    try {
      // Para pagos de productos, usamos el endpoint de transacciones directamente
      if (paymentData.type === 'product_purchase') {
        const walletPaymentData = {
          ...paymentData,
          paymentMethod: 'wallet'
        };
        
        // Simular pago con monedero (esto debería ser un endpoint específico)
        const response = await api.post('/wallet/pay-product', walletPaymentData);
        return response.data;
      }
      
      // Para pagos de partidos
      if (paymentData.type === 'match_payment') {
        const response = await api.post(ENDPOINTS.WALLET_PAY_MATCH, {
          matchId: paymentData.relatedTo,
          amount: paymentData.amount
        });
        return response.data;
      }

      throw new Error('Tipo de pago no soportado para monedero');
    } catch (error) {
      console.error('❌ Error al pagar con monedero:', error);
      throw error;
    }
  },

  /**
   * Obtener transacciones del usuario
   */
  getUserTransactions: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      
      Object.keys(filters).forEach(key => {
        if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
          params.append(key, filters[key]);
        }
      });

      const queryString = params.toString();
      const url = queryString ? `${ENDPOINTS.TRANSACTIONS}?${queryString}` : ENDPOINTS.TRANSACTIONS;

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error al obtener transacciones:', error);
      throw error;
    }
  },

  /**
   * Obtener detalles de una transacción
   */
  getTransactionById: async (id) => {
    try {
      const response = await api.get(ENDPOINTS.TRANSACTION_BY_ID(id));
      return response.data;
    } catch (error) {
      console.error('❌ Error al obtener transacción:', error);
      throw error;
    }
  },

  /**
   * Obtener saldo del monedero
   */
  getWalletBalance: async () => {
    try {
      const response = await api.get(ENDPOINTS.WALLET_BALANCE);
      return response.data;
    } catch (error) {
      console.error('❌ Error al obtener saldo del monedero:', error);
      throw error;
    }
  },

  /**
   * Obtener transacciones del monedero
   */
  getWalletTransactions: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      
      Object.keys(filters).forEach(key => {
        if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
          params.append(key, filters[key]);
        }
      });

      const queryString = params.toString();
      const url = queryString ? `${ENDPOINTS.WALLET_TRANSACTIONS}?${queryString}` : ENDPOINTS.WALLET_TRANSACTIONS;

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error al obtener transacciones del monedero:', error);
      throw error;
    }
  },

  /**
   * Procesar reembolso (solo admin)
   */
  processRefund: async (transactionId, refundData) => {
    try {
      const response = await api.post(ENDPOINTS.REFUND(transactionId), refundData);
      return response.data;
    } catch (error) {
      console.error('❌ Error al procesar reembolso:', error);
      throw error;
    }
  },

  /**
   * Obtener estadísticas de pagos (solo admin)
   */
  getPaymentStats: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      
      Object.keys(filters).forEach(key => {
        if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
          params.append(key, filters[key]);
        }
      });

      const queryString = params.toString();
      const url = queryString ? `${ENDPOINTS.STATS}?${queryString}` : ENDPOINTS.STATS;

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error al obtener estadísticas de pagos:', error);
      throw error;
    }
  },

  /**
   * Formatear precio
   */
  formatPrice: (price, currency = 'EUR') => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: currency,
    }).format(price);
  },

  /**
   * Formatear estado de transacción
   */
  formatTransactionStatus: (status) => {
    const statusMap = {
      'pending': 'Pendiente',
      'processing': 'Procesando',
      'completed': 'Completada',
      'failed': 'Fallida',
      'cancelled': 'Cancelada',
      'refunded': 'Reembolsada'
    };
    return statusMap[status] || status;
  },

  /**
   * Obtener color para el estado de transacción
   */
  getStatusColor: (status) => {
    const colorMap = {
      'pending': '#ffc107',
      'processing': '#17a2b8',
      'completed': '#28a745',
      'failed': '#dc3545',
      'cancelled': '#6c757d',
      'refunded': '#fd7e14'
    };
    return colorMap[status] || '#6c757d';
  },

  /**
   * Validar datos de pago
   */
  validatePaymentData: (paymentData) => {
    const errors = {};

    if (!paymentData.amount || paymentData.amount <= 0) {
      errors.amount = 'El monto debe ser mayor a 0';
    }

    if (paymentData.amount > 10000) {
      errors.amount = 'El monto máximo permitido es €10,000';
    }

    if (!paymentData.type) {
      errors.type = 'El tipo de transacción es obligatorio';
    }

    if (!paymentData.description) {
      errors.description = 'La descripción es obligatoria';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  },

  /**
   * Redirigir a Redsys para pago
   */
  redirectToRedsys: (redsysForm) => {
    console.log('🔄 redirectToRedsys llamado con:', redsysForm);

    if (!redsysForm || !redsysForm.redsysUrl) {
      console.error('❌ Datos de Redsys incompletos:', redsysForm);
      throw new Error('Datos de Redsys incompletos');
    }

    console.log('✅ Datos de Redsys válidos, creando formulario...');
    console.log('🌐 URL de Redsys:', redsysForm.redsysUrl);

    // Crear formulario dinámico para enviar a Redsys
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = redsysForm.redsysUrl;

    // Añadir campos ocultos
    const fields = {
      'Ds_SignatureVersion': redsysForm.Ds_SignatureVersion,
      'Ds_MerchantParameters': redsysForm.Ds_MerchantParameters,
      'Ds_Signature': redsysForm.Ds_Signature
    };

    console.log('📝 Campos del formulario:', fields);

    Object.keys(fields).forEach(key => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = fields[key];
      form.appendChild(input);
    });

    // Añadir al DOM y enviar
    document.body.appendChild(form);
    console.log('🚀 Enviando formulario a Redsys...');
    form.submit();
  },

  /**
   * Forzar inscripción después del pago
   */
  forceInscription: async (inscriptionData) => {
    try {
      const response = await api.post('/payments/force-inscription', inscriptionData);
      return response.data;
    } catch (error) {
      console.error('❌ Error al forzar inscripción:', error);
      throw error;
    }
  },

  /**
   * Obtener informes financieros completos (Admin)
   */
  getFinancialReports: async (params = {}) => {
    try {
      const response = await api.get(ENDPOINTS.FINANCIAL_REPORTS, { params });
      return response.data;
    } catch (error) {
      console.error('❌ Error al obtener informes financieros:', error);
      throw error;
    }
  },

  /**
   * Obtener transacciones para administradores
   */
  getAdminTransactions: async (filters = {}) => {
    try {
      const response = await api.get(ENDPOINTS.ADMIN_TRANSACTIONS, { params: filters });
      return response.data;
    } catch (error) {
      console.error('❌ Error al obtener transacciones de admin:', error);
      throw error;
    }
  },

  /**
   * Obtener estadísticas de transferencias
   */
  getTransferStats: async () => {
    try {
      const response = await api.get(ENDPOINTS.TRANSFER_STATS);
      return response.data;
    } catch (error) {
      console.error('❌ Error al obtener estadísticas de transferencias:', error);
      throw error;
    }
  },

  /**
   * Validar transferencia bancaria
   */
  validateTransfer: async (transferId) => {
    try {
      const response = await api.post(ENDPOINTS.VALIDATE_TRANSFER(transferId));
      return response.data;
    } catch (error) {
      console.error('❌ Error al validar transferencia:', error);
      throw error;
    }
  },

  /**
   * Rechazar transferencia bancaria
   */
  rejectTransfer: async (transferId, reason) => {
    try {
      const response = await api.post(ENDPOINTS.REJECT_TRANSFER(transferId), { reason });
      return response.data;
    } catch (error) {
      console.error('❌ Error al rechazar transferencia:', error);
      throw error;
    }
  },

  /**
   * Crear transacción pendiente (transferencia, bizum)
   */
  createPendingTransaction: async (paymentData) => {
    try {
      console.log('📤 Creando transacción pendiente:', paymentData);
      const response = await api.post(ENDPOINTS.CREATE_PENDING, paymentData);
      return response.data;
    } catch (error) {
      console.error('❌ Error al crear transacción pendiente:', error);
      throw error;
    }
  },

  /**
   * Procesar inscripción directa (pago en efectivo por admin)
   */
  processDirectInscription: async (inscripcionData) => {
    try {
      console.log('📤 Procesando inscripción directa:', inscripcionData);
      const response = await api.post(ENDPOINTS.DIRECT_INSCRIPTION, inscripcionData);
      return response.data;
    } catch (error) {
      console.error('❌ Error al procesar inscripción directa:', error);
      throw error;
    }
  }
};

export default paymentService;
